import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Button, Table, Typography } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { OPEN_ROOM } from 'gql/appointments/mutation';
import { GET_APPOINTMENT_BY_CREATOR } from 'gql/appointments/queries';
import {
  AppointmentByCreatorDoctor_appointmentByCreatorDoctor_data_results as AppointmentType,
  AppointmentByCreatorDoctor
} from 'gql/appointments/__generated__/AppointmentByCreatorDoctor';
import { OpenRoom } from 'gql/appointments/__generated__/OpenRoom';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import { useAppDispatch } from 'store/hooks';
import { setTour } from 'store/tour/slice';

import { PageWrapper } from 'components/ui';
import useCopyText from 'hooks/useCopyText';
import { showErrorMessage } from 'utils/showErrorMessage';
import { getIdStart } from 'utils/string';
import { instance } from 'utils/api';
import { tokenStorage } from 'utils/storage';
import { CreateVideoCall } from './components';
import { HowItWorksLink } from 'components/shared';

const PAGE_LIMIT = 10;

const { Title, Paragraph } = Typography;

const VideoCall: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const { copyText } = useCopyText();
  const [links, setLinks] = useState<Record<string, string>>({});
  const [passwords, setPasswords] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  const { data, loading, refetch } = useQuery<AppointmentByCreatorDoctor>(
    GET_APPOINTMENT_BY_CREATOR,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        query: {
          page,
          limit: PAGE_LIMIT
        }
      },
      onCompleted() {
        dispatch(setTour({ key: 'videoCall', value: true }));
      }
    }
  );

  const [openRoom] = useMutation<OpenRoom>(OPEN_ROOM, {
    onError: err => showErrorMessage(err)
  });

  const onCreate = () => {
    setPage(0);
    refetch();
  };

  const onCopyRoom = useCallback(
    async (id: string) => {
      const token = await tokenStorage.get();
      openRoom({ variables: { input: { id } } })
        .then(({ data }) => {
          const url = data?.openRoom.data.redirect_url;
          const roomId = data?.openRoom.data.meetUrl;

          if (url) {
            const link = `${url}/home/${roomId}?creator=doctor`;

            setLinks(prev => ({ ...prev, [id]: link }));
            copyText(link);

            return instance.post(
              'meetings/create-room/doctor',
              {
                appointmentId: id
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
          }
        })
        .then(res => {
          setPasswords(prev => ({
            ...prev,
            [id]: res?.data?.data?.password
          }));
        });
    },
    [copyText, openRoom]
  );

  const columns: ColumnProps<AppointmentType>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: id => getIdStart(id)
      },
      {
        title: t('sessions.note'),
        dataIndex: 'notes',
        key: 'notes',
        render(value) {
          return <p style={{ maxWidth: 200 }}>{value?.[0]?.note || '-'}</p>;
        }
      },
      {
        title: t('sessions.link'),
        dataIndex: 'id',
        key: 'link',
        width: '30%',
        render: id =>
          links[id] ? (
            <Paragraph
              data-element="video-call-link"
              style={{ whiteSpace: 'nowrap' }}
              copyable={{ text: links[id] }}
            >
              {`${links[id].slice(0, 40)}...`}
            </Paragraph>
          ) : (
            '-'
          )
      },
      {
        title: t('sessions.password'),
        dataIndex: 'id',
        key: 'password',
        render: id =>
          passwords[id] ? <Paragraph copyable>{passwords[id]}</Paragraph> : '-'
      },
      {
        title: t('sessions.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: '25%',
        render: createdAt => dayjs(createdAt).format(FULL_DATE_FORMAT)
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'actions',
        align: 'right',
        render: id => (
          <Button
            data-element="video-call-generate-button"
            type="default"
            size="middle"
            onClick={() => onCopyRoom(id)}
          >
            {t('sessions.password_and_link')}
          </Button>
        )
      }
    ],
    [t, links, passwords, onCopyRoom]
  );

  const results = data?.appointmentByCreatorDoctor?.data.results || [];
  const total = data?.appointmentByCreatorDoctor?.data.total || 0;

  return (
    <PageWrapper
      title={t('video_call.title')}
      color="white"
      extra={[
        <HowItWorksLink
          key="how-it-works"
          type={DOCTOR_HOW_IT_WORKS_PAGES.VIDEO_CALL.key}
          title={t('common.how_it_works')}
        />
      ]}
    >
      <CreateVideoCall onCreate={onCreate} />
      <Title level={3}>{t('video_call.previous_video_call_link')}</Title>
      {/* <Space size={16} style={{ marginBottom: 16 }}>
        <Input.Search
          size="large"
          style={{ width: 289 }}
          placeholder={t('video_call.search_by_id')}
        />
        <Space size={8}>
          <Button type="primary" size="large">
            {t('common.apply')}
          </Button>
          <Button type="default" size="large">
            {t('common.reset')}
          </Button>
        </Space>
      </Space> */}
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          pageSize: PAGE_LIMIT,
          onChange: p => setPage(p - 1),
          showSizeChanger: false
        }}
        scroll={{ x: 768 }}
      />
    </PageWrapper>
  );
};

export default VideoCall;
